import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const Dashboard = () => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/dashboard/games?limit=100`);
      setGames(response.data.games);
      setError(null);
    } catch (err) {
      console.error('Error fetching games:', err);
      setError('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const fetchBidsForGame = async (gameId) => {
    try {
      setBidsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/dashboard/games/${gameId}/bids`);
      setBids(response.data.bids);
      setSelectedGame(gameId);
    } catch (err) {
      console.error('Error fetching bids:', err);
      setError('Failed to load bids');
    } finally {
      setBidsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Game Dashboard</h1>

      <div style={styles.stats}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Games</div>
          <div style={styles.statValue}>{games.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Games</div>
          <div style={styles.statValue}>
            {games.filter(g => g.status === 'active').length}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Completed Games</div>
          <div style={styles.statValue}>
            {games.filter(g => g.status === 'completed').length}
          </div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <h2 style={styles.subtitle}>Games</h2>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Game ID</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Start Time</th>
              <th style={styles.th}>Real Bids</th>
              <th style={styles.th}>Free Bids</th>
              <th style={styles.th}>Real Pot</th>
              <th style={styles.th}>Free Pot</th>
              <th style={styles.th}>Combined Pot</th>
              <th style={styles.th}>Real LWB</th>
              <th style={styles.th}>Free LWB</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {games.map((game) => (
              <tr
                key={game.id}
                style={{
                  ...styles.row,
                  backgroundColor: selectedGame === game.id ? '#1a1f3a' : 'transparent'
                }}
              >
                <td style={styles.td}>{game.id.substring(0, 8)}...</td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: game.status === 'active' ? '#00ff88' : '#8b92b0'
                  }}>
                    {game.status}
                  </span>
                </td>
                <td style={styles.td}>{formatDate(game.start_time)}</td>
                <td style={styles.td}>{game.real_bid_count}</td>
                <td style={styles.td}>{game.free_bid_count}</td>
                <td style={styles.td}>{formatAmount(game.real_pot)}</td>
                <td style={styles.td}>{formatAmount(game.free_pot)}</td>
                <td style={styles.td}>{formatAmount(game.combined_pot_calculated)}</td>
                <td style={styles.td}>
                  {game.lowest_winning_bid_real ? formatAmount(game.lowest_winning_bid_real) : '-'}
                </td>
                <td style={styles.td}>
                  {game.free_track_lwb ? formatAmount(game.free_track_lwb) : '-'}
                </td>
                <td style={styles.td}>
                  <button
                    style={styles.button}
                    onClick={() => fetchBidsForGame(game.id)}
                  >
                    View Bids
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedGame && (
        <div style={styles.bidsContainer}>
          <h2 style={styles.subtitle}>
            Bids for Game {selectedGame.substring(0, 8)}...
            {bidsLoading && <span style={styles.loadingText}> Loading...</span>}
          </h2>
          {!bidsLoading && bids.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>Winner</th>
                  <th style={styles.th}>Wallet</th>
                </tr>
              </thead>
              <tbody>
                {bids.map((bid) => (
                  <tr key={bid.id} style={styles.row}>
                    <td style={styles.td}>{formatAmount(bid.amount)}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.typeBadge,
                        backgroundColor: bid.is_free ? '#00d4ff' : '#00ff88'
                      }}>
                        {bid.is_free ? 'Free' : 'Real'}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(bid.timestamp)}</td>
                    <td style={styles.td}>{bid.is_winner ? '✅' : '-'}</td>
                    <td style={styles.td}>
                      {bid.wallet_address ? bid.wallet_address.substring(0, 10) + '...' : 'Anonymous'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!bidsLoading && bids.length === 0 && (
            <div style={styles.noBids}>No bids for this game</div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    backgroundColor: '#343957',
    color: '#ffffff',
    minHeight: '100vh',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '30px',
    color: '#00d4ff',
  },
  subtitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#ffffff',
  },
  stats: {
    display: 'flex',
    gap: '20px',
    marginBottom: '40px',
  },
  statCard: {
    flex: 1,
    backgroundColor: '#1a1f3a',
    padding: '20px',
    borderRadius: '12px',
    border: '2px solid #2a3055',
  },
  statLabel: {
    fontSize: '14px',
    color: '#8b92b0',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#00d4ff',
  },
  tableContainer: {
    marginBottom: '40px',
  },
  bidsContainer: {
    marginTop: '40px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#1a1f3a',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  headerRow: {
    backgroundColor: '#2a3055',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    borderBottom: '2px solid #0a0e27',
  },
  row: {
    borderBottom: '1px solid #2a3055',
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '12px',
    fontSize: '14px',
    color: '#ffffff',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#0a0e27',
  },
  typeBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#0a0e27',
  },
  button: {
    backgroundColor: 'transparent',
    border: '2px solid #00d4ff',
    color: '#00d4ff',
    padding: '6px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    transition: 'all 0.3s',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px',
    color: '#8b92b0',
  },
  loadingText: {
    fontSize: '16px',
    color: '#8b92b0',
    marginLeft: '10px',
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px',
    color: '#ff4466',
  },
  noBids: {
    textAlign: 'center',
    padding: '20px',
    color: '#8b92b0',
  },
};

export default Dashboard;
